// Same-origin in production (Nginx proxies /api). In dev, Vite proxies /api.
const BASE = '';

export class ApiError extends Error {
  constructor(public status: number, message?: string) {
    super(message ?? `Request failed (${status})`);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let message: string | undefined;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      message = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
    } catch {
      /* no JSON error body */
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(p: string) => request<T>('GET', p),
  post: <T>(p: string, b?: unknown) => request<T>('POST', p, b),
  patch: <T>(p: string, b?: unknown) => request<T>('PATCH', p, b),
  del: <T>(p: string) => request<T>('DELETE', p),
};

// --- Response shapes ---
export interface Me {
  user: { id: string; email: string; fullName: string | null; role: { key: string; name: string | null } };
  tenant: { id: string; name: string; schema: string };
  provider: string;
  permissions: string[];
  /** Per-tenant feature modules enabled (gates verticals/optional features in the UI). */
  modules: string[];
}

export interface Providers {
  microsoft: boolean;
  google: boolean;
  devLogin: boolean;
}

export interface ListResult<T = Record<string, unknown>> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface FilterCondition {
  field: string;
  operator: string;
  value?: unknown;
}

export interface CustomFieldDef {
  id: string;
  objectType: string;
  fieldKey: string;
  label: string;
  dataType: string;
  options?: string[] | null;
  isRequired: boolean;
  sortOrder: number;
}

export interface TagDef {
  id: string;
  name: string;
  color: string | null;
}

export interface SavedView {
  id: string;
  objectType: string;
  name: string;
  filterDef: { logic?: string; conditions: FilterCondition[] };
  isShared: boolean;
}

export interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface SearchResults {
  accounts: { id: string; name: string; industry?: string }[];
  contacts: { id: string; fullName: string | null; email: string | null }[];
  deals: { id: string; name: string; amount?: unknown; status?: string }[];
}

export interface PipelineWithStages {
  id: string;
  name: string;
  isDefault: boolean;
  stages: { id: string; name: string; sortOrder: number; isWon: boolean; isLost: boolean }[];
}
